import { Logger } from "./logger";
import { getFetchUrl, fetchData } from "./models/data-service";

export interface BannerCreative {
    id?: string;
    width?: number;
    height?: number;
    iframeResourceUrl: string;
    imageUrl?: string;
    impressionUrls: string[];
    completeUrl?: string;
}

class MultiBannerAds {
    vastUrl: string = "";
    creatives: BannerCreative[] = [];
    adImpressionUrl?: string;
    currentDownload: Promise<void> = Promise.resolve();

    constructor(adTagUrl: string) {
        this.vastUrl = adTagUrl ? getFetchUrl(adTagUrl, true) : "";
    }


    fetchAndResolve = (): Promise<BannerCreative[]> => {
        Logger.printLog('info', 'MultiBannerAds.fetchAndResolve() - called', this.vastUrl);
        if (this.vastUrl == "") {
            return Promise.resolve([]);
        }
        return fetchData(this.vastUrl)
            .then((response: any) => response?.data)
            .then((data: string) => {
                if (!data) {
                    return [];
                }
                const parsed = this.parseVastResponse(data);
                this.adImpressionUrl = parsed.adImpressionUrl;
                this.creatives = parsed.creatives;
                if (this.creatives.length === 0) {
                    return [];
                }
                return this.downloadImage(0).then(() => this.creatives);
            })
            .catch((err) => {
                Logger.printLog('error', 'MultiBannerAds.fetchAndResolve() - VAST fetch failed', this.vastUrl);
                return [];
            });
    }

    parseVastResponse = (responseVast: any): { adImpressionUrl?: string; creatives: BannerCreative[] } => {
        const creatives: BannerCreative[] = [];
        const response = (responseVast instanceof Document ? responseVast : $.parseXML(responseVast));
        const inLine = $(response).find('InLine').first();

        if (!inLine || inLine.length === 0) {
            Logger.printLog('error', 'MultiBannerAds.parseVastResponse() - no InLine node found', '');
            return { creatives };
        }

        const adImpressionUrl = inLine.children('Impression').first().text().trim() || undefined;
        inLine.find('Creative').each((i: number, creativeEl: any) => {
            const nonLinearAds = $(creativeEl).find('NonLinearAds').first();
            const nonLinear = nonLinearAds.find('NonLinear').first();
            const iframeResourceUrl = nonLinear.find('IFrameResource').first().text().trim();

            if (!iframeResourceUrl) {
                Logger.printLog('error', 'MultiBannerAds.parseVastResponse() - Creative missing IFrameResource, skipped', i);
                return;
            }

            const creative: BannerCreative = {
                id: $(creativeEl).attr('AdID'),
                width: parseInt(nonLinear.attr('width')) || undefined,
                height: parseInt(nonLinear.attr('height')) || undefined,
                iframeResourceUrl,
                impressionUrls: []
            };

            nonLinearAds.find('Impression').each((i2: number, impEl: any) => {
                const url = $(impEl).text().trim();
                if (url) creative.impressionUrls.push(url);
            });

            nonLinearAds.find('Tracking').each((i2: number, trkEl: any) => {
                if ($(trkEl).attr('event') == 'complete') {
                    creative.completeUrl = $(trkEl).text().trim();
                }
            });

            creatives.push(creative);
        });

        Logger.printLog('data', 'MultiBannerAds.parseVastResponse() - creatives: ', creatives);
        return { adImpressionUrl, creatives };
    }

    resolveCreativeImage = (creative: BannerCreative): Promise<void> => {
        const url = getFetchUrl(creative.iframeResourceUrl, true);
        return fetchData(url)
            .then((response: any) => response?.data)
            .then((data: any) => {
                const info = data?.info;
                creative.imageUrl = info?.fhd?.url || info?.hd?.url || info?.sd?.url;
                if (!creative.imageUrl) {
                    Logger.printLog('error', 'MultiBannerAds.resolveCreativeImage() - no image url in response', data);
                    return;
                }
                return creative.imageUrl;
            })
            .catch((err) => {
                Logger.printLog('error', 'MultiBannerAds.resolveCreativeImage() - fetch failed', creative.iframeResourceUrl);
            });
    }

    downloadImage = async (index: number): Promise<void> => {
        const creative = this.creatives[index];
        if (!creative || creative.imageUrl) {
            return;
        }        
        Logger.printLog('info', 'MultiBannerAds.downloadImage() - downloading index', index);
        await this.resolveCreativeImage(creative);
    }

    prefetchNextBanner = (currentIndex: number): void => {
        if (this.creatives.length === 0) {
            return;
        }
        const nextIndex = (currentIndex + 1) % this.creatives.length;
        this.currentDownload = this.currentDownload.then(() => this.downloadImage(nextIndex));
    }

    pingTracker = (url?: string): void => {
        if (!url) return;
        fetch(url).catch(() => {
            Logger.printLog('error', 'MultiBannerAds.pingTracker() - failed', url);
        });
    }

    fireAdImpression = (): void => {
        this.pingTracker(this.adImpressionUrl);
    }

    fireImpressions = (creative: BannerCreative): void => {
        creative.impressionUrls.forEach(this.pingTracker);
    }

    fireTrackingEvent = (creative: BannerCreative): void => {
        this.pingTracker(creative.completeUrl);
    }
}



export default MultiBannerAds;
